// Zone = a single ingested Lesson, played as a series of encounters.
//
// Encounter sequence: 8–10 monsters → mini-boss → chest → zone boss.
// Cards come from the SRS draw on entry. If we run out (kid does scaffold
// step-3 a lot), we top up from the lesson's full card list.
//
// On boss fight we hand off to the BossScene; for MVP the BossScene
// is just CombatScene with a fatter HP pool — keeping it simple per
// the plan's "Phase 1 ships extra HP, not full Phase-2 tone trial."

import Phaser from 'phaser';
import { db, newId, type Lesson, type Session } from '../../data/db';
import { drawEncounter } from '../../learning/srs';
import { appState } from '../../app/state';
import type { Card } from '../../content/cardSchema';
import { CombatScene, type CombatResult } from './CombatScene';
import { COLORS, drawMonster, drawParallaxBg, drawWizard, monsterMeta } from '../util/draw';
import { grantXp, XP_REWARDS } from '../../app/xp';

interface Spawn {
  kind: 'monster' | 'chest' | 'miniBoss' | 'boss';
  x: number;
  hp: number;
  defeated: boolean;
  monsterKind: number;
  gfx?: Phaser.GameObjects.Graphics;
  label?: Phaser.GameObjects.Text;
}

interface ZoneInit { lessonId: string }

export class ZoneScene extends Phaser.Scene {
  private lesson!: Lesson;
  private cards: Card[] = [];
  private session!: Session;
  private hero!: Phaser.GameObjects.Graphics;
  private heroX = 0;
  private heroTargetX = 0;
  private spawns: Spawn[] = [];
  private currentIdx = 0;
  private bg!: Phaser.GameObjects.Graphics;
  private statusText!: Phaser.GameObjects.Text;

  constructor() { super({ key: 'Zone' }); }

  private cfg: ZoneInit = { lessonId: '' };

  init(data: ZoneInit) {
    this.cfg = data;
  }

  async create() {
    // Register CombatScene if not present
    if (!this.scene.get('Combat')) {
      this.scene.add('Combat', CombatScene, false);
    }

    const lesson = await db.lessons.get(this.cfg.lessonId);
    if (!lesson) { this.scene.start('Academy'); return; }
    this.lesson = lesson;

    const profile = appState.get().profile!;
    this.cards = await drawEncounter({
      profileId: profile.id,
      lessonId: lesson.id,
      size: profile.ageBand === '4-5' ? '6' : '10',
    });
    if (this.cards.length === 0) {
      this.cards = await db.cards.where('lessonId').equals(lesson.id).toArray();
    }
    if (this.cards.length === 0) { this.scene.start('Academy'); return; }

    this.session = {
      id: newId(),
      profileId: profile.id,
      startedAt: Date.now(),
      encountersCompleted: 0,
      xpGained: 0,
    };
    await db.sessions.put(this.session);

    const { width, height } = this.scale;
    this.bg = drawParallaxBg(this, COLORS.bg);

    // Header bar
    this.add.rectangle(0, 0, width, 40, COLORS.panel, 0.85).setOrigin(0, 0);
    this.add.text(12, 20, `${this.lesson.title}`, { color: '#f5efff', fontSize: '14px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0, 0.5);
    this.statusText = this.add.text(width - 12, 20, '', { color: '#f5efff', fontSize: '12px', fontFamily: '-apple-system, sans-serif' }).setOrigin(1, 0.5).setAlpha(0.7);
    const exitBtn = this.add.text(width / 2, 20, '← Academy', { color: '#f5efff', fontSize: '12px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5).setAlpha(0.85);
    exitBtn.setInteractive({ useHandCursor: true }).on('pointerdown', () => appState.goto({ kind: 'academy' }));

    // Hero
    this.hero = this.add.graphics();
    this.heroX = 80;
    this.heroTargetX = 80;
    drawWizard(this.hero, this.heroX, height * 0.72, 1.0);

    // Lay out spawns evenly across the zone
    const count = Math.min(this.cards.length, 10);
    const margin = 160;
    const spacing = Math.max(140, (width - margin * 2) / Math.max(1, count - 1));
    for (let i = 0; i < count; i++) {
      const x = margin + i * spacing;
      const isMiniBoss = i === Math.floor(count / 2);
      const isBoss = i === count - 1;
      const kind: Spawn['kind'] = isBoss ? 'boss' : isMiniBoss ? 'miniBoss' : 'monster';
      this.spawns.push({
        kind,
        x,
        hp: kind === 'boss' ? 5 : kind === 'miniBoss' ? 3 : 1,
        defeated: false,
        monsterKind: (i + Math.floor(Math.random() * 3)) % 6,
      });
    }
    // Insert a chest just before the boss
    const chestX = margin + (count - 1) * spacing - 70;
    this.spawns.splice(this.spawns.length - 1, 0, {
      kind: 'chest',
      x: chestX,
      hp: 1,
      defeated: false,
      monsterKind: 0,
    });

    this.spawns.forEach((s) => this.drawSpawn(s));
    this.updateStatus();
    this.advance();
  }

  private drawSpawn(s: Spawn) {
    const { height } = this.scale;
    s.gfx = this.add.graphics();
    if (s.kind === 'chest') {
      s.gfx.fillStyle(COLORS.gold, 1);
      s.gfx.fillRoundedRect(s.x - 22, height * 0.65, 44, 28, 6);
      s.gfx.fillStyle(0x8a6a18, 1);
      s.gfx.fillRect(s.x - 22, height * 0.65 + 12, 44, 4);
      s.label = this.add.text(s.x, height * 0.55, 'chest', { color: '#ffe27a', fontSize: '11px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5);
    } else {
      const scale = s.kind === 'boss' ? 1.8 : s.kind === 'miniBoss' ? 1.4 : 1.0;
      drawMonster(s.gfx, s.x, height * 0.66, s.monsterKind, scale);
      const tag = s.kind === 'boss' ? 'BOSS' : s.kind === 'miniBoss' ? 'mini boss' : '';
      if (tag) {
        s.label = this.add.text(s.x, height * 0.52, tag, {
          color: s.kind === 'boss' ? '#ff7a4d' : '#ffe27a',
          fontSize: s.kind === 'boss' ? '14px' : '12px',
          fontFamily: '-apple-system, sans-serif',
          fontStyle: 'bold',
        }).setOrigin(0.5);
      }
    }
  }

  private updateStatus() {
    const remaining = this.spawns.filter((s) => !s.defeated).length;
    this.statusText.setText(`encounters left: ${remaining}`);
  }

  private async advance() {
    const next = this.spawns.find((s) => !s.defeated);
    if (!next) return this.complete();

    // Walk hero to it
    this.heroTargetX = next.x - 80;
    await new Promise<void>((res) => {
      this.tweens.add({
        targets: { x: this.heroX },
        x: this.heroTargetX,
        duration: Math.abs(this.heroTargetX - this.heroX) * 6,
        onUpdate: (_, t) => {
          this.heroX = t.x;
          this.hero.clear();
          drawWizard(this.hero, this.heroX, this.scale.height * 0.72, 1.0);
        },
        onComplete: () => res(),
      });
    });

    // Start the right encounter
    if (next.kind === 'chest') return this.openChest(next);
    return this.startCombat(next);
  }

  private async openChest(s: Spawn) {
    const profile = appState.get().profile!;
    const progress = appState.get().progress!;
    // A chest "key" is a random card we've seen before — stealth review.
    const seen = await db.srs.where('profileId').equals(profile.id).and((x) => x.reps > 0).toArray();
    let card: Card | null = null;
    if (seen.length > 0) {
      const pick = seen[Math.floor(Math.random() * seen.length)];
      card = (await db.cards.get(pick.cardId)) ?? null;
    }
    card = card ?? this.cards[Math.floor(Math.random() * this.cards.length)];

    this.scene.launch('Combat', {
      card,
      monsterKind: 5,
      monsterMaxHp: 1,
      baseDamage: 1,
      sessionId: this.session.id,
      onResolved: async (result: CombatResult) => {
        s.defeated = true;
        if (s.label) s.label.setText(result.passed ? 'opened!' : 'closed');
        if (s.gfx) s.gfx.setAlpha(0.4);
        if (result.passed) {
          const { unlocks, leveledUp } = await grantXp(profile.id, XP_REWARDS.chestOpened);
          progress.coins += 5;
          await db.progress.put(progress);
          await appState.refreshProgress();
          if (leveledUp || unlocks.length) this.flashLevelUp(unlocks.map((u) => u.label).join('  ·  '));
        }
        this.session.encountersCompleted += 1;
        await db.sessions.put(this.session);
        this.updateStatus();
        this.advance();
      },
    });
  }

  private async startCombat(s: Spawn) {
    const profile = appState.get().profile!;
    const card = this.cards[this.currentIdx % this.cards.length];
    this.currentIdx += 1;

    const baseDamage = 1; // monster hp is 1 for normal, 3 for mini, 5 for boss — so 1 dmg/turn means N turns
    this.scene.launch('Combat', {
      card,
      monsterKind: s.monsterKind,
      monsterMaxHp: s.hp,
      baseDamage,
      sessionId: this.session.id,
      onResolved: async (result: CombatResult) => {
        if (result.ranAway && !result.passed) {
          // Step-3 scaffold: monster flees, encounter completes but card re-queues.
          s.defeated = true;
          if (s.gfx) this.tweens.add({ targets: s.gfx, x: 40, alpha: 0.2, duration: 400 });
          if (s.label) s.label.setText('escaped');
          this.cards.push(card);
        } else if (result.passed) {
          s.defeated = true;
          if (s.gfx) this.tweens.add({ targets: s.gfx, alpha: 0, y: 30, duration: 280, ease: 'Quad.easeIn' });
          if (s.label) s.label.setText('✓');

          const progress = appState.get().progress!;
          let xp = XP_REWARDS.kill;
          if (result.outcome === 'critical') xp += XP_REWARDS.crit;
          if (s.kind === 'boss') xp += XP_REWARDS.bossDefeated;
          const { unlocks, leveledUp } = await grantXp(profile.id, xp);
          progress.coins += s.kind === 'boss' ? 20 : s.kind === 'miniBoss' ? 8 : 3;
          await db.progress.put(progress);
          await appState.refreshProgress();
          if (leveledUp || unlocks.length) this.flashLevelUp(unlocks.map((u) => u.label).join('  ·  '));
        } else {
          // Shouldn't really happen — combat resolution always ends in pass or run-away.
        }

        // Check for hero KO
        const progress = appState.get().progress!;
        if (progress.hp <= 0) {
          return this.heroDown();
        }

        this.session.encountersCompleted += 1;
        await db.sessions.put(this.session);
        this.updateStatus();
        this.advance();
      },
    });
  }

  private async complete() {
    const profile = appState.get().profile!;
    const progress = appState.get().progress!;
    // Mark lesson unlocked in their progression so the unlock chain advances.
    if (!progress.unlockedZoneIds.includes(this.lesson.id)) {
      progress.unlockedZoneIds.push(this.lesson.id);
      await db.progress.put(progress);
      await appState.refreshProgress();
    }
    this.session.endedAt = Date.now();
    await db.sessions.put(this.session);

    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width * 0.7, 200, COLORS.panel, 0.95).setStrokeStyle(2, COLORS.gold);
    this.add.text(width / 2, height / 2 - 50, 'Zone cleared!', { color: '#ffe27a', fontSize: '24px', fontFamily: '-apple-system, sans-serif', fontStyle: 'bold' }).setOrigin(0.5);
    this.add.text(width / 2, height / 2 - 10, `${this.session.encountersCompleted} encounters · ${progress.coins} coins`, { color: '#f5efff', fontSize: '14px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5).setAlpha(0.85);
    const back = this.add.text(width / 2, height / 2 + 40, '→ Back to Academy', { color: '#4dd672', fontSize: '14px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5);
    back.setInteractive({ useHandCursor: true }).on('pointerdown', () => appState.goto({ kind: 'academy' }));
  }

  private async heroDown() {
    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width * 0.7, 180, COLORS.panel, 0.95).setStrokeStyle(2, COLORS.bad);
    this.add.text(width / 2, height / 2 - 30, 'You wobble back…', { color: '#ff7a4d', fontSize: '22px', fontFamily: '-apple-system, sans-serif', fontStyle: 'bold' }).setOrigin(0.5);
    this.add.text(width / 2, height / 2 + 4, 'Keep your XP and items. Try again tomorrow.', { color: '#f5efff', fontSize: '13px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5).setAlpha(0.8);
    // Restore some HP for next time, and sting 20% coins
    const progress = appState.get().progress!;
    progress.hp = Math.max(1, Math.round(progress.maxHp * 0.6));
    progress.coins = Math.round(progress.coins * 0.8);
    await db.progress.put(progress);
    await appState.refreshProgress();
    const back = this.add.text(width / 2, height / 2 + 40, '→ Back to Academy', { color: '#4dd672', fontSize: '14px', fontFamily: '-apple-system, sans-serif' }).setOrigin(0.5);
    back.setInteractive({ useHandCursor: true }).on('pointerdown', () => appState.goto({ kind: 'academy' }));
  }

  private flashLevelUp(label: string) {
    const { width, height } = this.scale;
    const txt = this.add.text(width / 2, height * 0.32, `LEVEL UP! ${label || ''}`, { color: '#ffe27a', fontSize: '18px', fontFamily: '-apple-system, sans-serif', fontStyle: 'bold' }).setOrigin(0.5);
    this.tweens.add({ targets: txt, alpha: 0, y: txt.y - 30, duration: 1800, onComplete: () => txt.destroy() });
  }
}
